// @flow

import * as React from 'react';
import moment from 'moment';
import 'moment-timezone';
import { css } from '@emotion/core';

import mq from "../../../Styles/breakpoints";
import type {Schedules} from "../Container";
import type {Airing, Episode, Show} from "../../Objects";
import type {Client as ShowClient} from '../../../ShowPage/APIClient';
import Expansion from "./Expansion";
import {sortChannels} from "../../helpers";
import {getWindow, isMobileSafari} from "../../../Helpers/browsers";
import ScheduleChannel from "./ScheduleChannel";
import Interrupt from "./Interrupt";
import ScheduleTimes from "./ScheduleTimes";
import {event} from "../../../Helpers/ga";
import {APP_NAME} from "../../App";
import getGrid from "./grid";
import type {Grid} from "./grid";

type Props = {|
  start: number,
  schedules: Schedules,
  interval: number,
  getAiring: number => Airing | null,
  getEpisode: number => Episode | null,
  getShow: number => Show | null,
  firstCellRef: { current: null | React.ElementRef<any> },
  gridRef: { current: null | React.ElementRef<any> },
  getShowAPIClient: () => ShowClient,
  currentChannel: number,
  changeChannel: (number, bool) => void;
|};

export type ExpansionConfig = {
  airingId: number,
  location: number,
} | null;

type State = {|
  keyboardEnabled: bool,
  expansion: ExpansionConfig,
  loadTime: number
|};

class ScheduleGrid extends React.Component<Props, State> {
  loadTimeRef: { current: null | React.ElementRef<'div'> };
  returnRef: { current: null | React.ElementRef<'div'> };

  constructor(props: Props) {
    super(props);

    this.loadTimeRef = React.createRef();
    this.returnRef = React.createRef();

    let now = moment();

    let minutes = parseInt(now.format('H')) * 60 +
      parseInt(now.format('m'));

    let loadTime = Math.floor(minutes / props.interval ) - 1;

    this.handleScroll = this.handleScroll.bind(this);
    this.makeExpansionHandler = this.makeExpansionHandler.bind(this);

    this.state = {
      keyboardEnabled: false,
      expansion: null,
      loadTime: loadTime
    };
  }

  componentDidMount(): void {
      getWindow().then(window => {
        if (this.loadTimeRef.current) {
          const top = this.loadTimeRef.current.getBoundingClientRect().top + window.pageYOffset;
          const middle = Math.max(top - (window.innerHeight / 2), 0);

          window.scrollTo({
            top: middle,
            behavior: 'smooth'
          });
        }

        if (this.props.gridRef.current !== null) {
          this.props.gridRef.current.addEventListener('scroll', this.handleScroll);
        }
      });
  }

  componentWillUnmount() {
    if (this.props.gridRef.current !== null) {
      this.props.gridRef.current.removeEventListener('scroll', this.handleScroll);
    }
  };

  handleScroll: Function;
  handleScroll(e: SyntheticEvent<HTMLDivElement>) {
    let { currentChannel } = this.props;

    //if scrolling more than halfway to a channel, change channel
    let channels = Math.round((e.currentTarget.scrollWidth - 80) / (e.currentTarget.offsetWidth - 80));

    //possibly split out left scroll vs right school so expansion doesn't immediately close when scrolling left
    for(let i = channels; i > 0; i--) {
      let midpoint = (i * (e.currentTarget.offsetWidth - 80)) - ((e.currentTarget.offsetWidth - 80) / 2);

      if (e.currentTarget.scrollLeft >= midpoint) {
        if (i !== currentChannel) {
          event(APP_NAME, 'Swipe to Change Channel', i);
          this.props.changeChannel(i, false);
        }

        break;
      } else if (i === 1) {
        if (0 !== currentChannel) {
          event(APP_NAME, 'Swipe to Change Channel', i);
          this.props.changeChannel(0, false);
        }
      }
    }
  }

  makeExpansionHandler: (string, number, number) => void;
  makeExpansionHandler(showTitle: string, airingId: number, location: number) {
    return () => {
      event(APP_NAME, 'Activate Expansion', `${showTitle} | ${airingId} | ${location}`);
      this.setState({expansion: {airingId, location}});
    };
  }

  closeExpansion() {
    this.setState({ expansion: null });

    if (this.returnRef.current) {
      this.returnRef.current.focus();
    }
  }

  enableOutlines(e: SyntheticKeyboardEvent<any>) {
    event(APP_NAME, 'Keyboard Enabled', e.key);

    this.setState({
      keyboardEnabled: true
    })
  }

  /**
   * Mobile Safari seems to be broken in the way it handles z-indexing with the
   * combination of position: sticky, position: fixed, and display: grid.
   *
   * Instead we render a separate modal specifically for them in a different
   * location in the DOM
   *
   * @param msRows
   * @returns {null|*}
   */
  renderMobileSafariExpansion({ msRows }: Grid) {
    if (this.state.expansion && isMobileSafari()) {
      let { airingId, location } = this.state.expansion;
      let { keyboardEnabled } = this.state;
      let { getAiring, getEpisode, getShow, getShowAPIClient } = this.props;

      let airing = getAiring(airingId);

      if (airing) {
        let episode = getEpisode(airing.episode);
        let show = getShow(airing.show);

        if (episode && show) {
          let expansionPlacement = css`
            position: fixed;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            padding: 15px;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 4;

            ${mq.large(`
              position: relative;
              width: 100%;
              padding: 0;
              background: none;
              display: block;
              grid-row: expansion-${location};
              grid-column-start: 1;
              grid-column-end: -1;
              z-index: 3;
              
              -ms-grid-row: ${msRows.get(`expansion-${location}`) || 0};
              -ms-grid-column: 1;  
              -ms-grid-column-span: 5;
            `)}
          `;

          return <div css={expansionPlacement} key={`${airingId}-expansion`}>
            <Expansion
              airing={airing}
              episode={episode}
              show={show}
              onClose={this.closeExpansion.bind(this)}
              showAPIClient={getShowAPIClient}
              keyboardEnabled={keyboardEnabled}
            />
          </div>;
        }
      }
    }

    return null;
  }

  renderDefaultExpansion({ msRows }: Grid) {
    if (this.state.expansion && !isMobileSafari()) {
      let { airingId, location } = this.state.expansion;
      let { keyboardEnabled } = this.state;
      let { getAiring, getEpisode, getShow, getShowAPIClient } = this.props;

      let airing = getAiring(airingId);

      if (airing) {
        let episode = getEpisode(airing.episode);
        let show = getShow(airing.show);

        if (episode && show) {
          let expansionPlacement = css`
            position: fixed;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            padding: 15px;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 4;

            ${mq.large(`
              position: relative;
              width: 100%;
              padding: 0;
              background: none;
              display: block;
              grid-row: expansion-${location};
              grid-column-start: 1;
              grid-column-end: -1;
              z-index: 3;
              
              -ms-grid-row: ${msRows.get(`expansion-${location}`) || 0};
              -ms-grid-column: 1;  
              -ms-grid-column-span: 5;
            `)}
          `;

          return <div css={expansionPlacement} key={`${airingId}-expansion`}>
            <Expansion
              airing={airing}
              episode={episode}
              show={show}
              onClose={this.closeExpansion.bind(this)}
              showAPIClient={getShowAPIClient}
              keyboardEnabled={keyboardEnabled}
            />
          </div>;
        }
      }
    }

    return null;
  }

  render() {
    if (this.props.schedules.size === 0) {
      return null;
    }

    let sample = this.props.schedules.values().next().value;

    if (sample) {
      let { interval, schedules, start, getAiring, getEpisode, getShow, firstCellRef } = this.props;
      let { keyboardEnabled, loadTime, expansion } = this.state;
      let rowCount = sample.airings.length;
      let channels = sortChannels([...schedules.keys()]);
      let grid = getGrid(rowCount, channels);

      return <React.Fragment>
        <div css={grid.styles} ref={this.props.gridRef} onKeyDown={!keyboardEnabled ? this.enableOutlines.bind(this) : null}>
          <ScheduleTimes
            msRows={grid.msRows}
            rowCount={rowCount}
            interval={interval}
            start={start}
            loadTime={loadTime}
            loadTimeRef={this.loadTimeRef} />
          {
            channels.map((key, i) => {
              return <ScheduleChannel
                key={key}
                // $FlowFixMe - Relying on type defs to verify that airings will be available
                airings={schedules.get(key).airings}
                msRows={grid.msRows}
                msCols={grid.msCols}
                keyboardEnabled={keyboardEnabled}
                expansion={expansion}
                getAiring={getAiring}
                getEpisode={getEpisode}
                getShow={getShow}
                firstCellRef={i === 0 ? firstCellRef : null}
                returnRef={this.returnRef}
                clickFactory={this.makeExpansionHandler}
              />;
            })
          }
          <Interrupt name="morning" msRows={grid.msRows} />
          <Interrupt name="afternoon" msRows={grid.msRows} />
          <Interrupt name="primetime" msRows={grid.msRows} />
          <Interrupt name="late-night" msRows={grid.msRows} />
          {this.renderDefaultExpansion(grid)}
        </div>
        {this.renderMobileSafariExpansion(grid)}
      </React.Fragment>;
    }

    return null;
  }
}

export default ScheduleGrid;